/**
 * Spec 9.4: a 22 px outlined "i" at ~55 % opacity on the **last card of the topic only**,
 * opening a white bottom sheet with a text block and the image credits. The links here are the
 * one permitted exception to "no links out" (ADR 0005) and go through the `platform` module.
 *
 * The text block's wording is fixed by spec 9.4; the image lines are built from the columns
 * `publish` wrote, with "verkleinert" when the pipeline resized the original and
 * "KI-generiert" when the tier is AI.
 */

import { useEffect, useRef } from "react";
import type { Credit } from "../../shared/api-types.js";
import { ExternalLink } from "../../shared/platform.js";
import { Button } from "../../shared/primitives/Button.js";
import { CloseIcon, InfoIcon } from "../../shared/primitives/Icons.js";

export function CreditButton({ onOpen }: { onOpen: () => void }) {
  return (
    <Button
      onClick={onOpen}
      aria-label="Quellen und Lizenzen zu diesem Thema"
      className="absolute bottom-[14px] left-5 z-3 text-white opacity-55"
    >
      <InfoIcon />
    </Button>
  );
}

function sourceName(site: string): string {
  return site.includes("wikipedia") ? "aus der Wikipedia" : "aus dem Klexikon";
}

function imageCreditLine(image: Credit["images"][number]): string {
  const parts = [image.author, image.licence];
  if (image.modified) parts.push("zugeschnitten/verkleinert");
  if (image.aiGenerated) parts.push("KI-generiert");
  return parts.filter(Boolean).join(", ");
}

export function CreditSheet({
  credit,
  topicTitle,
  open,
  onClose,
}: {
  credit: Credit;
  topicTitle: string;
  open: boolean;
  onClose: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) closeRef.current?.focus();
  }, [open]);

  return (
    <div
      className={
        "absolute inset-x-0 bottom-0 z-5 max-h-[70%] overflow-y-auto rounded-t-[20px] bg-white px-5 pt-[18px] pb-7 text-[13px] leading-[1.45] text-[#222] shadow-[0_-10px_40px_rgba(0,0,0,.3)] transition-transform duration-250 " +
        (open ? "translate-y-0" : "translate-y-[105%]")
      }
      // `inert` keeps the closed sheet out of the tab order and off screen readers.
      inert={!open}
      // A swipe on the open sheet is the sheet's, never a page turn underneath it.
      data-own-scroll="always"
      aria-label="Quellen und Lizenzen"
    >
      <Button
        ref={closeRef}
        onClick={onClose}
        aria-label="Schliessen"
        className="absolute top-3 right-4 grid h-7 w-7 place-items-center rounded-full bg-[#eee] text-[#222]"
      >
        <CloseIcon />
      </Button>

      <h4 className="m-0 mb-1 text-[12px] tracking-[.08em] text-[#888] uppercase">Text</h4>
      <p className="mt-0 mb-3">
        Nach dem Artikel «{credit.text.sourceTitle}» {sourceName(credit.text.sourceSite)}, Autoren
        siehe{" "}
        <ExternalLink href={credit.text.sourceUrl} className="text-[#06c]">
          Versionsgeschichte
        </ExternalLink>
        . Lizenz{" "}
        <ExternalLink href={credit.text.licenceUrl} className="text-[#06c]">
          {credit.text.licence}
        </ExternalLink>
        . Von Scrolivo für Kinder umgeschrieben und gekürzt (mit KI); Karten und Fragen ebenfalls{" "}
        {credit.text.licence}.
      </p>

      {credit.images.length > 0 && (
        <>
          <h4 className="m-0 mb-1 text-[12px] tracking-[.08em] text-[#888] uppercase">
            {credit.images.length === 1 ? "Bild" : "Bilder"}
          </h4>
          <ul className="m-0 mb-1 list-none p-0">
            {credit.images.map((image) => (
              <li key={image.cardPosition} className="mb-2">
                Karte {image.cardPosition}: {imageCreditLine(image)}
                {image.licenceUrl && (
                  <>
                    {" "}
                    <ExternalLink href={image.licenceUrl} className="text-[#06c]">
                      Lizenz
                    </ExternalLink>
                  </>
                )}
                {image.sourceUrl && (
                  <>
                    {" "}
                    <ExternalLink href={image.sourceUrl} className="text-[#06c]">
                      Quelle
                    </ExternalLink>
                  </>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
      <p className="m-0 text-[#888]">Thema: {topicTitle}</p>
    </div>
  );
}
